// src/hooks/useDashboardData.ts
import { useState, useEffect } from 'react';
import {
  getFunnelMetrics,
  getFunnelTimeseries,
  getAiVsHumano,
  getTimeDistribution,
  getWeeklyHeatmap,
  FunnelMetricsResponse,
  TimeseriesItem,
  IAvsHumanoResponse,
  TimeDistributionItem,
  WeeklyHeatmapItem,
  FunnelMetricsParams
} from '../services/metricsApi.ts';

type FilterParams = {
  clinicId?: number;
  startDate?: string;
  endDate?: string;
};

type DashboardData = {
  funnelMetrics: FunnelMetricsResponse | null;
  timeseriesData: TimeseriesItem[];
  aiVsHumanoData: IAvsHumanoResponse | null;
  timeDistribution: TimeDistributionItem[];
  weeklyHeatmap: WeeklyHeatmapItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  clinicId: number | null;
  clinicName: string | null;
};

/**
 * Obtém o ID da clínica do localStorage
 */
const getClinicIdFromStorage = (): number | null => {
  try {
    // Tenta obter o clinic_id do localStorage
    const rawClinicId = localStorage.getItem('clinic_id');
    if (rawClinicId) {
      return parseInt(rawClinicId, 10);
    }

    // Se não encontrou, verifica outras possibilidades
    // Como obter de um objeto 'clinic'
    const clinicDataStr = localStorage.getItem('clinic');
    if (clinicDataStr) {
      const clinicData = JSON.parse(clinicDataStr);
      if (clinicData?.clinic?.id) {
        return Number(clinicData.clinic.id);
      }
      
      // Se temos a URL do logo, tentamos extrair o ID
      if (clinicData?.clinic?.logo_url) {
        const match = clinicData.clinic.logo_url.match(/clinic_(\d+)_logo/);
        if (match && match[1]) {
          return Number(match[1]);
        }
      }
    }
    
    // Verifica se temos em 'userData'
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      if (userData && userData.clinic_id) {
        return Number(userData.clinic_id);
      }
    }
    
    return null;
  } catch (err) {
    console.error("Erro ao obter o ID da clínica:", err);
    return null;
  }
};

/**
 * Obtém o nome da clínica do localStorage
 */
const getClinicNameFromStorage = (): string | null => {
  try {
    const clinicDataStr = localStorage.getItem('clinic');
    if (clinicDataStr) {
      const clinicData = JSON.parse(clinicDataStr);
      if (clinicData?.clinic?.name_clinic) {
        return clinicData.clinic.name_clinic;
      }
    }
    
    return null;
  } catch (err) {
    console.error("Erro ao obter o nome da clínica:", err);
    return null;
  }
};

/**
 * Hook para carregamento de dados do dashboard
 * Usa automaticamente o clinicId do localStorage
 */
export const useDashboardData = (filters: FilterParams): DashboardData => {
  const [funnelMetrics, setFunnelMetrics] = useState<FunnelMetricsResponse | null>(null);
  const [timeseriesData, setTimeseriesData] = useState<TimeseriesItem[]>([]);
  const [aiVsHumanoData, setAiVsHumanoData] = useState<IAvsHumanoResponse | null>(null);
  const [timeDistribution, setTimeDistribution] = useState<TimeDistributionItem[]>([]);
  const [weeklyHeatmap, setWeeklyHeatmap] = useState<WeeklyHeatmapItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [clinicName, setClinicName] = useState<string | null>(null);

  // Obtém o clinicId do localStorage no carregamento inicial
  useEffect(() => {
    try {
      // Exibe todos os dados do localStorage para debug
      console.log("Conteúdo do localStorage:", {
        clinicId: localStorage.getItem('clinic_id'),
        clinic: localStorage.getItem('clinic'),
        userData: localStorage.getItem('userData')
      });
      
      // Obtém o ID da clínica
      const id = getClinicIdFromStorage();
      setClinicId(id);
      
      // Obtém o nome da clínica
      const name = getClinicNameFromStorage();
      setClinicName(name);
      
      console.log('ID da clínica obtido:', id);
      console.log('Nome da clínica obtido:', name);
    } catch (err) {
      console.error("Erro ao obter dados da clínica:", err);
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Determina qual clinicId usar (prioriza o do filter, se fornecido)
      const effectiveClinicId = filters.clinicId || clinicId || undefined;
      
      // Se não temos clinicId, exibe mensagem de erro
      if (!effectiveClinicId) {
        setError("Não foi possível identificar a clínica. Por favor, faça login novamente.");
        setLoading(false);
        return;
      }

      console.log('Carregando dados para a clínica ID:', effectiveClinicId);

      // Prepara os parâmetros para a chamada das APIs
      const apiParams: FunnelMetricsParams = {
        clinicId: effectiveClinicId,
        startDate: filters.startDate,
        endDate: filters.endDate
      };

      console.log('Parâmetros para as APIs:', apiParams);

      // Carregar dados em paralelo usando as funções do metricsApi.ts
      const [
        metricsData,
        timeseriesResult,
        aiHumanoResult,
        timeDistResult,
        heatmapResult
      ] = await Promise.all([
        getFunnelMetrics(apiParams),
        getFunnelTimeseries(apiParams.clinicId, apiParams.startDate, apiParams.endDate),
        getAiVsHumano(apiParams.clinicId, apiParams.startDate, apiParams.endDate),
        getTimeDistribution(apiParams.clinicId, apiParams.startDate, apiParams.endDate),
        getWeeklyHeatmap(apiParams.clinicId, apiParams.startDate, apiParams.endDate)
      ]);
      
      console.log('Dados carregados com sucesso:', {
        funnelMetrics: metricsData,
        timeseries: timeseriesResult,
        aiVsHumano: aiHumanoResult,
        timeDistribution: timeDistResult,
        weeklyHeatmap: heatmapResult
      });
      
      setFunnelMetrics(metricsData);
      setTimeseriesData(timeseriesResult);
      setAiVsHumanoData(aiHumanoResult);
      setTimeDistribution(timeDistResult);
      setWeeklyHeatmap(heatmapResult);
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      setError("Ocorreu um erro ao carregar os dados. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Recarrega os dados quando os filtros ou o clinicId do localStorage mudarem
  useEffect(() => {
    if (clinicId !== null || filters.clinicId) {
      loadData();
    }
  }, [filters.clinicId, filters.startDate, filters.endDate, clinicId]);

  return {
    funnelMetrics,
    timeseriesData,
    aiVsHumanoData,
    timeDistribution,
    weeklyHeatmap,
    loading,
    error,
    refresh: loadData,
    clinicId,
    clinicName
  };
};